import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import Grid from "@material-ui/core/Grid";
import Chart from "react-apexcharts";
import greenCircleImg from "../../assets/images/icon-green-circle.svg";
import grayCircleImg from "../../assets/images/icon-gray-circle.svg";
import CellTowerIcon from '@mui/icons-material/CellTower';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import LightIcon from '@mui/icons-material/Light';
import { Typography, IconButton, Switch, FormControlLabel } from "@material-ui/core";
import { Stack } from "@mui/material";
import { LineChart } from "@mui/x-charts";
import DateRangeIcon from '@mui/icons-material/DateRange';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { getSystemUpTimeData } from "../../query/dash-chart-query";
import DeviceDataService from "../../apis/device-data";
import LampDataService from "../../apis/lamps-data";
import { getIsAdmin } from "../../apis/auth";

// Individual Trend Chart Component for better performance
const TrendChart = React.memo(({
  type,
  chartData,
  viewMode,
  availableYears,
  isLoading,
  lastUpdate,
  currentYearActual,
  onYearChange
}) => {
  const data = chartData[type];
  const isYearly = viewMode === 'yearly';

  let yearlyData = data.yearly;
  if (isYearly) {
    yearlyData = data.yearly.filter(item => item.value > 0);
  }

  const chartDataValues = isYearly
    ? yearlyData.map(item => item.value)
    : data.daily.values;

  const chartLabels = isYearly
    ? yearlyData.map(item => item.month)
    : data.daily.labels;

  const lastValue = chartDataValues?.length > 0
    ? chartDataValues[chartDataValues.length - 1]
    : 0;

  const color = type === 'slc' ? '#f283aa' : type === 'lamp' ? '#4CAF50' : '#2196F3';
  const icon = type === 'slc' ? <DateRangeIcon style={{ color: "red" }} /> :
    type === 'lamp' ? <DateRangeIcon style={{ color: "green" }} /> :
      <EnergySavingsLeafIcon style={{ color: "blue" }} />;
  const title = type === 'slc' ? 'SLC Status Trend' :
    type === 'lamp' ? 'Lamp Trend' : 'Energy Saving';


  const currentIndex = availableYears.indexOf(parseInt(data.year, 10));

  // Check if can go to previous year
  const canGoToPrev = currentIndex > 0;

  // Check if can go to next year and not beyond actual current year
  const canGoToNext = currentIndex < availableYears.length - 1;

  return (
    <div className="dash-chart-style">
      <div className="dash-chart-header-wrapper">
        <div className="w-70">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {icon}
              <h4 style={{ margin: 0, marginLeft: '10px' }}>{title}</h4>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {isYearly && (
                <>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onYearChange(type, 'prev');
                    }}
                    size="small"
                    disabled={!canGoToPrev || isLoading}
                  >
                    <ArrowLeftIcon />
                  </IconButton>
                  <Typography variant="body1" style={{ margin: '0 8px' }}>
                    {data.year}
                  </Typography>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onYearChange(type, 'next');
                    }}
                    size="small"
                    disabled={!canGoToNext || isLoading}
                  >
                    <ArrowRightIcon />
                  </IconButton>
                </>
              )}
            </div>
          </div>
        </div>
        <div style={{ fontWeight: "bold", marginTop: '4px', marginLeft: '10px' }}>
          {isLoading ? 'Loading...' : `${lastValue}%`}
        </div>
      </div>

      {isLoading ? (
        <div style={{ height: '230px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography>Loading data...</Typography>
        </div>
      ) : (
        <div className="dash-chart-body-wrapper">
          <Stack sx={{ width: '100%' }}>
            <LineChart
              width={400}
              height={230}
              colors={[color]}
              series={[{ data: chartDataValues || [] }]}
              xAxis={[{ scaleType: 'point', data: chartLabels || [] }]}
              yAxis={[{ min: 0, max: 100 }]}  // Use the calculated y-axis config
            />
          </Stack>
        </div>
      )}
      {/* {chartLabels.length > 0 ? (
        <div style={{ fontSize: '14px', color: '#888' }}>
          {!isLoading && <span>Last update {lastUpdate} hours ago</span>}
          {!isYearly && <span> | Showing last 10 days</span>}
        </div>
      ) : (
        <div style={{ fontSize: '14px', color: '#888' }}>
          {!isLoading && <span>No data to display</span>}
        </div>
      )} */}
    </div>
  );
});

const DashChart = ({ apiResponseData = {}, isDataLoading = false }) => {
  const panelsStreetlightsInfo = useSelector(
    (state) => state.dashboardReducer.panelsStreetlightsInfo
  );

  // Separate loading states for each card
  const [loadingStates, setLoadingStates] = useState({
    slc: false,
    lamp: false,
    energy: false,
    initial: true
  });

  // State for chart data and available years
  const [chartData, setChartData] = useState({
    slc: { yearly: [], daily: [], year: new Date().getFullYear() },
    lamp: { yearly: [], daily: [], year: new Date().getFullYear() },
    energy: { yearly: [], daily: [], year: new Date().getFullYear() }
  });

  const [lastUpdate, setLastUpdate] = useState(0);
  const [availableYears, setAvailableYears] = useState([]);
  const [switchData, setSwitchData] = useState({ slc: [], lmp: [], evs: [] });
  const [viewMode, setViewMode] = useState('yearly'); // 'yearly' or 'daily'

  const currentDate = new Date();
  const currentYearActual = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const currentDay = currentDate.getDate();
  const [getWithSLC, setGetWithSLC] = useState(null);
  const [getWorkingLamps, setGetWorkingLamps] = useState(null);

  // Set loading state for specific card
  const setCardLoading = useCallback((cardType, isLoading) => {
    setLoadingStates(prev => ({
      ...prev,
      [cardType]: isLoading
    }));
  }, []);

  // Set initial loading state
  const setInitialLoading = useCallback((isLoading) => {
    setLoadingStates(prev => ({
      ...prev,
      initial: isLoading
    }));
  }, []);

  const fetchAvailableYears = useCallback(async () => {
    try {
      setInitialLoading(true);
      // Get only the last record from each collection
      const [hourData, dayData, energyData] = await Promise.all([
        getSystemUpTimeData("SlcStDly", 1, null, null, true),
        getSystemUpTimeData("LmpStDly", 1, null, null, true),
        getSystemUpTimeData("ensav", 1, null, null, true)
      ]);

      // Extract years from each data source separately
      const slcYears = new Set();
      const lmpYears = new Set();
      const evsYears = new Set();
      const years = new Set();

      [hourData, dayData, energyData].forEach(data => {
        if (data.length > 0 && data[0].dt?.seconds) {
          const year = new Date(data[0].dt.seconds * 1000).getFullYear();
          years.add(year);
        }
      });

      const sortedYears = Array.from(years).sort((a, b) => b - a);
      const currentYear = new Date().getFullYear();

      const lowestYear = sortedYears.length > 0 ? sortedYears[sortedYears.length - 1] : currentYear;

      const fullYearList = [];
      for (let y = lowestYear; y <= currentYear; y++) {
        fullYearList.push(y);
      }
      setAvailableYears(fullYearList);

      // Process SlcStDly data (hourData)
      hourData.forEach(data => {
        if (data.dt?.seconds) {
          const year = new Date(data.dt.seconds * 1000).getFullYear();
          slcYears.add(year);
        }
      });

      // Process LmpStDly data (dayData)
      dayData.forEach(data => {
        if (data.dt?.seconds) {
          const year = new Date(data.dt.seconds * 1000).getFullYear();
          lmpYears.add(year);
        }
      });

      // Process ensav data (energyData)
      energyData.forEach(data => {
        if (data.dt?.seconds) {
          const year = new Date(data.dt.seconds * 1000).getFullYear();
          evsYears.add(year);
        }
      });

      // Convert Sets to sorted arrays
      const slcSorted = Array.from(slcYears).sort((a, b) => b - a);
      const lmpSorted = Array.from(lmpYears).sort((a, b) => b - a);
      const evsSorted = Array.from(evsYears).sort((a, b) => b - a);

      // Create full year ranges for each source
      const currentYear1 = new Date().getFullYear();

      const createFullYearList = (years) => {
        if (years.length === 0) return [];
        const lowestYear = years[years.length - 1];
        const fullList = [];
        for (let y = lowestYear; y <= currentYear1; y++) {
          fullList.push(y);
        }
        return fullList;
      };

      const result = {
        slc: createFullYearList(slcSorted),
        lmp: createFullYearList(lmpSorted),
        evs: createFullYearList(evsSorted)
      };

      setSwitchData(result);
      return result;

    } catch (error) {
      console.error("Error fetching available years:", error);
      return availableYears || { slc: [], lmp: [], evs: [] };
    } finally {
      setInitialLoading(false);
    }
  }, []);


  const fetchYearlyData = useCallback(async (year, collectionName) => {
    try {
      const startDate = new Date(year, 0, 1);
      const endDate = new Date(year, 11, 31, 23, 59, 59);
      const startTimestamp = Math.floor(startDate.getTime() / 1000);
      const endTimestamp = Math.floor(endDate.getTime() / 1000);

      const data = await getSystemUpTimeData(collectionName, null, startTimestamp, endTimestamp);
      return data;
    } catch (error) {
      console.error(`Error fetching yearly data for ${collectionName}:`, error);
      return [];
    }
  }, []);

  const fetchDailyData = useCallback(async (collectionName) => {
    try {
      const data = await getSystemUpTimeData(collectionName, 10);
      return data;
    } catch (error) {
      console.error(`Error fetching daily data for ${collectionName}:`, error);
      return [];
    }
  }, []);

  const processYearlyData = useCallback((data, collectionName, currentYear) => {
    const monthlyData = Array(12).fill().map(() => ({ sum: 0, count: 0 }));

    data.forEach(item => {
      if (!item.dt?.seconds) return;

      const date = new Date(item.dt.seconds * 1000);
      const month = date.getMonth();
      const year = date.getFullYear();

      // Skip future months or days in current month if we're in the current year
      if (year === currentYearActual) {
        if (month > currentMonth) return;
        if (month === currentMonth && date.getDate() > currentDay) return;
      }

      if (collectionName === 'ensav') {
        monthlyData[month].sum += item.esp || 0;
      } else {
        const uptime = item.totCnt > 0 ? (item.onCnt / item.totCnt) * 100 : 0;
        monthlyData[month].sum += uptime;
      }
      monthlyData[month].count++;
    });

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return months.map((month, index) => {
      const data = monthlyData[index];
      return {
        month,
        value: data.count > 0 ? parseFloat((data.sum / data.count).toFixed(2)) : 0,
        isCurrent: (currentYearActual === currentYear && index === currentMonth)
      };
    });
  }, [currentYearActual, currentMonth, currentDay]);

  const processDailyData = useCallback((data, collectionName) => {
    const dailyValues = [];
    const dailyLabels = [];

    data.forEach(item => {
      if (!item.dt?.seconds) return;

      const date = new Date(item.dt.seconds * 1000);
      const dayLabel = `${date.getDate()} ${date.toLocaleString('default', { month: 'short' })}`;

      let value;
      if (collectionName === 'ensav') {
        value = item.esp || 0;
      } else {
        value = item.totCnt > 0 ? (item.onCnt / item.totCnt) * 100 : 0;
      }

      dailyValues.push(parseFloat(value.toFixed(2)));
      dailyLabels.push(dayLabel);
    });

    // Reverse to show most recent day last
    return {
      values: dailyValues.reverse(),
      labels: dailyLabels.reverse()
    };
  }, []);

  // Handle year change for a specific card - OPTIMIZED
  const handleYearChange = useCallback(async (cardType, direction) => {
    const currentYear = chartData[cardType].year;
    const currentIndex = availableYears.indexOf(currentYear);

    // Determine new index based on direction
    const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;

    // Check if new index is valid
    if (newIndex >= 0 && newIndex < availableYears.length) {
      const newYear = availableYears[newIndex];

      // Set loading only for this specific card
      setCardLoading(cardType, true);

      try {
        const collectionName = getCollectionName(cardType);
        const yearlyData = await fetchYearlyData(newYear, collectionName);
        const processedData = processYearlyData(yearlyData, collectionName, newYear);

        // Update only the specific card's data
        setChartData(prev => ({
          ...prev,
          [cardType]: {
            ...prev[cardType],
            yearly: processedData,
            year: newYear
          }
        }));
      } catch (error) {
        console.error(`Error changing year for ${cardType}:`, error);
      } finally {
        // Clear loading only for this specific card
        setCardLoading(cardType, false);
      }
    }
  }, [chartData, availableYears, fetchYearlyData, processYearlyData, setCardLoading]);

  const getCollectionName = useCallback((cardType) => {
    switch (cardType) {
      case 'slc': return 'SlcStDly';
      case 'lamp': return 'LmpStDly';
      case 'energy': return 'ensav';
      default: return '';
    }
  }, []);

  // Toggle between yearly and daily view
  const toggleViewMode = useCallback(() => {
    const newMode = viewMode === 'yearly' ? 'daily' : 'yearly';
    setViewMode(newMode);
  }, [viewMode]);

  const [lastUpdates, setLastUpdates] = useState({
    slc: 0,
    lamp: 0,
    energy: 0
  });
  // Initial data load
  useEffect(() => {
    // Modify the loadData function in the useEffect to calculate individual last updates:
    const loadData = async () => {
      if (isDataLoading) return;
      await fetchAvailableYears();
      const currentYear = new Date().getFullYear();

      try {
        // Fetch yearly data for all cards
        const [slcYearly, lampYearly, energyYearly] = await Promise.all([
          fetchYearlyData(currentYear, 'SlcStDly'),
          fetchYearlyData(currentYear, 'LmpStDly'),
          fetchYearlyData(currentYear, 'ensav')
        ]);

        // Fetch daily data for all cards
        const [slcDaily, lampDaily, energyDaily] = await Promise.all([
          fetchDailyData('SlcStDly'),
          fetchDailyData('LmpStDly'),
          fetchDailyData('ensav')
        ]);

        // Calculate last update times for each type
        const now = new Date();
        const calculateHoursDiff = (data) => {
          if (data.length > 0 && data[data.length - 1]?.dt?.seconds) {
            const lastDate = new Date(data[data.length - 1].dt.seconds * 1000);
            return Math.floor((now - lastDate) / (1000 * 60 * 60));
          }
          return 0;
        };

        setLastUpdates({
          slc: calculateHoursDiff(slcYearly),
          lamp: calculateHoursDiff(lampYearly),
          energy: calculateHoursDiff(energyYearly)
        });

        setChartData({
          slc: {
            yearly: processYearlyData(slcYearly, 'SlcStDly', currentYear),
            daily: processDailyData(slcDaily, 'SlcStDly'),
            year: currentYear
          },
          lamp: {
            yearly: processYearlyData(lampYearly, 'LmpStDly', currentYear),
            daily: processDailyData(lampDaily, 'LmpStDly'),
            year: currentYear
          },
          energy: {
            yearly: processYearlyData(energyYearly, 'ensav', currentYear),
            daily: processDailyData(energyDaily, 'ensav'),
            year: currentYear
          }
        });
      } catch (error) {
        console.error("Error loading initial data:", error);
      }
    };

    loadData();
  }, [fetchAvailableYears, fetchYearlyData, fetchDailyData, processYearlyData, processDailyData, isDataLoading]);

  // Memoized chart configuration to prevent unnecessary re-renders
  const [chartConfig, setChartConfig] = useState({
    switchPanelschartConfig: {
      series: [809, 0],
      options: {
        chart: { width: 380, type: "donut" },
        labels: ["ON", "OFF"],
        dataLabels: { enabled: false },
        colors: ["#6FC372", "#A4A4A8"],
        plotOptions: {
          fill: { colors: ["#6FC372", "#A4A4A8"] },
          pie: { startAngle: -90, endAngle: 90, offsetY: 10 }
        },
        grid: { padding: { bottom: -80 } },
        legend: { position: "right", horizontalAlign: "center", show: false }
      }
    },
    streetLightschartConfig: {
      series: [10117, 0],
      options: {
        chart: { width: 380, type: "donut" },
        labels: ["ON", "OFF"],
        dataLabels: { enabled: false },
        colors: ["#6FC372", "#A4A4A8"],
        plotOptions: {
          fill: { colors: ["#6FC372", "#A4A4A8"] },
          pie: { startAngle: -90, endAngle: 90, offsetY: 10 }
        },
        grid: { padding: { bottom: -80 } },
        legend: { position: "right", horizontalAlign: "center", show: false }
      }
    },
    espChartConfig: {
      series: [10117, 0],
      options: {
        chart: { width: 380, type: "donut" },
        labels: ["Saved Energy", ""],
        dataLabels: { enabled: false },
        colors: ["#6FC372", "#A4A4A8"],
        plotOptions: {
          fill: { colors: ["#6FC372", "#A4A4A8"] },
          pie: { startAngle: -90, endAngle: 90, offsetY: 10 }
        },
        grid: { padding: { bottom: -80 } },
        legend: { position: "right", horizontalAlign: "center", show: false }
      }
    },
    ilmChartConfig: {
      series: [10117, 0],
      options: {
        chart: { width: 380, type: "donut" },
        labels: ["ON", "OFF"],
        dataLabels: { enabled: false },
        colors: ["#6FC372", "#A4A4A8"],
        plotOptions: {
          fill: { colors: ["#6FC372", "#A4A4A8"] },
          pie: { startAngle: -90, endAngle: 90, offsetY: 10 }
        },
        grid: { padding: { bottom: -80 } },
        legend: { position: "right", horizontalAlign: "center", show: false }
      }
    }
  });

  const getWorkingLights = useCallback((lmpOnCnt, lmpCnt) => {
    const result = lmpCnt === 0 ? 0 : ((lmpOnCnt / lmpCnt) * 100).toFixed(2);
    return `${result}%`;
  }, []);

  const getWorkingIlms = useCallback((lmpOnCnt, lmpCnt) => {
    let result = 0;
    try {
      result = lmpCnt === 0 ? 0 : ((lmpOnCnt / lmpCnt) * 100).toFixed(2);
    } catch (e) {
      console.log(`Error in working ILM count : ${e}`);
    }
    if (result === "NaN") {
      result = 0;
    }
    return `${result}%`;
  }, []);

  // const getWithSLC = useMemo(async () => {
  //   var data = await DeviceDataService.getInstance().getWithSLCCount();
  //   return JSON.stringify(data);
  // }, []);


  useEffect(() => {
    if (apiResponseData) {
      const {
        slcNcCnt = 0,
        withSlcOffCnt = 0,
        slcErrCnt = 0,
        lmpCnt = 0,
        lmpOnCnt = 0,
        lmpOffCnt = 0,
        lmpErrCnt = 0,
        ilmCnt = 0,
        ilmOnCnt = 0,
        slcOnCnt = 0,
        slcOffCnt = 0,
        ilmOffCnt = 0,
        ilmErrCnt = 0,
        esp = 0,
      } = apiResponseData || {};

      // const { slcOnCnt = 0, slcOffCnt = 0, } = DeviceDataService.getInstance().getWorkingCCMS();

      let ilmOffCntAdjusted = ilmOffCnt + ilmErrCnt;
      if (ilmOffCntAdjusted === 0) {
        ilmOffCntAdjusted = 0.1;
      }

      setChartConfig((prevState) => ({
        ...prevState,
        switchPanelschartConfig: {
          ...prevState.switchPanelschartConfig,
          series: [slcOnCnt, slcOffCnt],
        },
        streetLightschartConfig: {
          ...prevState.streetLightschartConfig,
          series: [lmpOnCnt, lmpOffCnt],
        },
        ilmChartConfig: {
          ...prevState.ilmChartConfig,
          series: [ilmOnCnt, ilmOffCntAdjusted],
        },
        espChartConfig: {
          ...prevState.espChartConfig,
          series: [esp, 100 - esp],
        },
      }));
    }
    const fetchData = async () => {
      try {
        const result = await DeviceDataService.getInstance().getWorkingCCMS();
        setGetWithSLC(result);
        const lampResult = await LampDataService.getInstance().getWorkingLampsCount();
        setGetWorkingLamps(lampResult);
      } catch (err) {
        console.log(err)
      }
    };
    fetchData();
  }, [apiResponseData]);

  const { switchPanelschartConfig, streetLightschartConfig, ilmChartConfig, espChartConfig } = chartConfig;
  const {
    slcCnt = 0,
    withSlcCnt = 0,
    slcNcCnt = 0,
    slcErrCnt = 0,
    lmpCnt = 0,
    lmpOnCnt = 0,
    slcOnCnt = 0,
    lmpOffCnt = 0,
    slcOffCnt = 0,
    lmpErrCnt = 0,
    ilmCnt = 0,
    ilmOnCnt = 0,
    ilmOffCnt = 0,
    ilmErrCnt = 0,
    esp = 0,
    flag = false,
  } = apiResponseData || {};

   // const { slcOnCnt = 0, slcOffCnt = 0, slcCnt1 = 0 } = DeviceDataService.getInstance().getWorkingCCMS();
  
  if (getWithSLC != null) {
      if (flag) {
          console.log("Switch Panel counting is using mock data");
      } else {
          console.log(`Switch Panel counting is using ${getIsAdmin() ? "old method" : "new method"}`);
      }
  }

  const displaySlcOnCnt = flag ? slcOnCnt : (getWithSLC?.slcOnCnt || 0);
  const displaySlcOffCnt = flag ? slcOffCnt : (getWithSLC?.slcOffCnt || 0);
  const displaySlcTotalCnt = flag ? withSlcCnt : (getWithSLC?.slcCnt1 || 0);

  const displayLmpOnCnt = flag ? lmpOnCnt : (getWorkingLamps?.lmpOnCnt || 0);
  const displayLmpOffCnt = flag ? lmpOffCnt : (getWorkingLamps?.lmpOffCnt || 0);
  const displayLmpTotalCnt = flag ? lmpCnt : (getWorkingLamps?.lmpCnt || 0);

  const displayIlmOnCnt = flag ? ilmOnCnt : (getWorkingLamps?.ilmOnCnt || 0);
  const displayIlmErrCnt = flag ? ilmErrCnt : (getWorkingLamps?.ilmErrCnt || 0);
  const displayIlmOffCnt = flag ? ilmOffCnt : (getWorkingLamps?.ilmOffCnt || 0);
  const displayIlmTotalCnt = flag ? ilmCnt : (getWorkingLamps?.ilmCnt || 0);
  
  let displayIlmOffCntAdjusted = displayIlmOffCnt + displayIlmErrCnt;
  if (displayIlmOffCntAdjusted === 0 && !isDataLoading) {
    displayIlmOffCntAdjusted = 0.1;
  }

  return (
    <div className="dash-chart">
      <Grid container spacing={2}>
        {/* Switch Panel */}
        <Grid item xs={4} className="custom-MuiGrid-grid-xs-4">
          <div className="dash-chart-style">
            <div className="dash-chart-header-wrapper">
              <div className="w-70">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <CellTowerIcon style={{ color: "red", marginRight: '10px' }} />
                  <h4 style={{ margin: 0 }}>Switch Panel</h4>
                </div>
                <div>
                  <span>Total: </span>
                  <span className="font-bold">{slcCnt} / {displaySlcTotalCnt}</span>
                </div>
              </div>
              <div className="w-30" style={{ width: "35%" }}>
                <div style={{ color: "#6FC372", marginBottom: 5 }}>
                  <img src={greenCircleImg} className="circle-img" style={{ verticalAlign: 'middle' }} />
                  <span>ON: {displaySlcOnCnt}</span>
                </div>
                <div style={{ color: "#A4A4A8", marginBottom: 5 }}>
                  <img src={grayCircleImg} className="circle-img" style={{ verticalAlign: 'middle' }} />
                  <span>OFF: {displaySlcOffCnt}</span>
                </div>
              </div>
            </div>
            <div className="dash-chart-body-wrapper" style={{ position: "relative", display: 'flex', justifyContent: 'center' }}>
              <div className="chart-wrapper" style={{ minWidth: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Chart
                  options={switchPanelschartConfig.options}
                  series={[displaySlcOnCnt, displaySlcOffCnt]}
                  type={"donut"}
                  width={400}
                  height={400}
                />
                <div className="chart-legend-wrapper" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                  <div>
                    {isDataLoading ? (
                      <span style={{ fontWeight: "bold" }}>Counting...</span>
                    ) : (
                      <>
                        <span>Working CCMS: </span><br />
                        <span style={{ fontWeight: "bold" }}>
                          {`${displaySlcTotalCnt === 0 ? 0 : ((displaySlcOnCnt / displaySlcTotalCnt) * 100).toFixed(2)}%`}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Grid>

        {/* Street Lights */}
        <Grid item xs={4} className="custom-MuiGrid-grid-xs-4">
          <div className="dash-chart-style">
            <div className="dash-chart-header-wrapper">
              <div className="w-70">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <LightbulbIcon style={{ color: "green", marginRight: '10px' }} />
                  <h4 style={{ margin: 0 }}>Street Lights</h4>
                </div>
                <div>
                  <span>Total: </span>
                  <span className="font-bold">{displayLmpTotalCnt}</span>
                </div>
              </div>
              <div className="w-30" style={{ width: "35%" }}>
                <div style={{ color: "#6FC372", marginBottom: 5 }}>
                  <img src={greenCircleImg} className="circle-img" style={{ verticalAlign: 'middle' }} />
                  <span>ON: {displayLmpOnCnt}</span>
                </div>
                <div style={{ color: "#A4A4A8", marginBottom: 5 }}>
                  <img src={grayCircleImg} className="circle-img" style={{ verticalAlign: 'middle' }} />
                  <span>OFF: {displayLmpOffCnt}</span>
                </div>
              </div>
            </div>
            <div className="dash-chart-body-wrapper" style={{ position: "relative", display: 'flex', justifyContent: 'center' }}>
              <div className="chart-wrapper" style={{ minWidth: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Chart
                  options={streetLightschartConfig.options}
                  series={[displayLmpOnCnt, displayLmpOffCnt]}
                  type={"donut"}
                  width={400}
                  height={400}
                />
                <div className="chart-legend-wrapper" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                  <div>
                    {isDataLoading ? (
                      <span style={{ fontWeight: "bold" }}>Counting...</span>
                    ) : (
                      <>
                        <span>Working Lights: </span><br />
                        <span style={{ fontWeight: "bold" }}>
                          {getWorkingLights(displayLmpOnCnt, displayLmpTotalCnt)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Grid>

        {/* ILM Status */}
        <Grid item xs={4} className="custom-MuiGrid-grid-xs-4">
          <div className="dash-chart-style">
            <div className="dash-chart-header-wrapper">
              <div className="w-70">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <LightIcon style={{ color: "orange", marginRight: '10px' }} />
                  <h4 style={{ margin: 0 }}>ILM Status</h4>
                </div>
                <div>
                  <span>Total: </span>
                  <span className="font-bold">{displayIlmTotalCnt}</span>
                </div>
              </div>
              <div className="w-30" style={{ width: "35%" }}>
                <div style={{ color: "#6FC372", marginBottom: 5 }}>
                  <img src={greenCircleImg} className="circle-img" style={{ verticalAlign: 'middle' }} />
                  <span style={{ fontSize: '15px' }}>ON: {displayIlmOnCnt}</span>
                </div>
                <div style={{ color: "#A4A4A8", marginBottom: 5 }}>
                  <img src={grayCircleImg} className="circle-img" style={{ verticalAlign: 'middle' }} />
                  <span style={{
                    fontSize: '15px'
                  }}>OFF: {displayIlmOffCnt}</span>
                </div>
              </div>
            </div>
            <div className="dash-chart-body-wrapper" style={{ position: "relative", display: 'flex', justifyContent: 'center' }}>
              <div className="chart-wrapper" style={{ minWidth: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Chart
                  options={ilmChartConfig.options}
                  series={[displayIlmOnCnt, displayIlmOffCntAdjusted]}
                  type={"donut"}
                  width={400}
                  height={400}
                />
                <div className="chart-legend-wrapper" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                  <div>
                    {isDataLoading ? (
                      <span style={{ fontWeight: "bold" }}>Counting...</span>
                    ) : (
                      <>
                        <span>Working ILM: </span><br />
                        <span style={{ fontWeight: "bold" }}>
                          {getWorkingIlms(displayIlmOnCnt, displayIlmTotalCnt)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginRight: 10 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={viewMode === 'yearly'}
                  onChange={toggleViewMode}
                  color="primary"
                />
              }
              label={viewMode === 'yearly' ? 'Month View' : 'Daily View'}
              labelPlacement="start"
            />
          </div>
        </Grid>
        {/* Trend Charts */}
        {/* Trend Charts */}
        <Grid item xs={4} className="custom-MuiGrid-grid-xs-4">
          <TrendChart
            type="slc"
            chartData={chartData}
            viewMode={viewMode}
            availableYears={switchData.slc}
            isLoading={loadingStates.slc || loadingStates.initial}
            lastUpdate={lastUpdates.slc}
            currentYearActual={currentYearActual}
            onYearChange={handleYearChange}
          />
        </Grid>
        <Grid item xs={4} className="custom-MuiGrid-grid-xs-4">
          <TrendChart
            type="lamp"
            chartData={chartData}
            viewMode={viewMode}
            availableYears={switchData.lmp}
            isLoading={loadingStates.lamp || loadingStates.initial}
            lastUpdate={lastUpdates.lamp}
            currentYearActual={currentYearActual}
            onYearChange={handleYearChange}
          />
        </Grid>
        <Grid item xs={4} className="custom-MuiGrid-grid-xs-4">
          <TrendChart
            type="energy"
            chartData={chartData}
            viewMode={viewMode}
            availableYears={switchData.evs}
            isLoading={loadingStates.energy || loadingStates.initial}
            lastUpdate={lastUpdates.energy}
            currentYearActual={currentYearActual}
            onYearChange={handleYearChange}
          />
        </Grid>
      </Grid>


    </div>
  );
};

export default DashChart;